// client/src/contexts/FeatureFlagsContext.js

import React, { createContext, useState, useEffect, useContext } from 'react';
import axiosInstance from '../axiosInstance';

// Create the context
const FeatureFlagsContext = createContext();

/**
 * Provider component for feature flags
 */
export const FeatureFlagsProvider = ({ children }) => {
  const [featureFlags, setFeatureFlags] = useState({
    enableGamesFeature: true,
    enableConsolesFeature: true,
    enableLegoFeature: true,
    enableMoviesFeature: true,
    enableMusicFeature: true,
    enableBooksFeature: true,
    enableBoardGamesFeature: false,
    enableAiChatFeature: false,
    enableMessageBoardFeature: false,
    enableFollowSystem: false,
    enableActivityFeed: false,
    enableReputationSystem: false,
    enableTrustModeration: false,
    enableSuggestions: false,
    enableReviews: false,
    enableItemGallery: false,
    enableComments: false,
    enableChatRooms: false,
    enableNotifications: false,
    requireEmailVerification: false,
    enableTradingCardsFeature: false,
    enableMangaFeature: false,
    enableComicsFeature: false,
    enableCustomCollections: false,
    enableFederation: false,
    // [seo-strip-start]
    enableDiscovery: false,
    // [seo-strip-end]
    enableQuotaHarvesting: true,
    enableBacklog: true,
    enableDNF: false,
    enableStreaks: false,
    enableDistribution: false,
    enableRicherReactions: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch feature flags on mount
  useEffect(() => {
    const fetchFeatureFlags = async () => {
      try {
        setLoading(true);
        
        // Use the configured axiosInstance which includes the auth token
        const response = await axiosInstance.get('/settings', {
          timeout: 5000 // 5 second timeout
        });
        
        console.log('Feature flags response:', response.data);
        
        // Set feature flags from response, with defaults if not found
        // For backward compatibility, check both new flags and old flag
        const moviesFlagEnabled = response.data.enableMoviesFeature !== undefined 
          ? response.data.enableMoviesFeature 
          : (response.data.enablePhysicalMediaFeature || false);
          
        const musicFlagEnabled = response.data.enableMusicFeature !== undefined
          ? response.data.enableMusicFeature
          : (response.data.enablePhysicalMediaFeature || true);
        
        const flags = {
          enableGamesFeature: response.data.enableGamesFeature !== undefined ? response.data.enableGamesFeature : true,
          enableConsolesFeature: response.data.enableConsolesFeature !== undefined ? response.data.enableConsolesFeature : true,
          enableLegoFeature: response.data.enableLegoFeature !== undefined ? response.data.enableLegoFeature : true,
          enableMoviesFeature: moviesFlagEnabled,
          enableMusicFeature: musicFlagEnabled,
          enableBooksFeature: response.data.enableBooksFeature !== undefined ? response.data.enableBooksFeature : true,
          enableBoardGamesFeature: response.data.enableBoardGamesFeature !== undefined ? response.data.enableBoardGamesFeature : false,
          enableAiChatFeature: response.data.enableAiChatFeature || false,
          enableMessageBoardFeature: response.data.enableMessageBoardFeature || false,
          enableFollowSystem: response.data.enableFollowSystem || false,
          enableActivityFeed: response.data.enableActivityFeed || false,
          enableReputationSystem: response.data.enableReputationSystem || false,
          enableTrustModeration: response.data.enableTrustModeration || false,
          enableSuggestions: response.data.enableSuggestions || false,
          enableReviews: response.data.enableReviews || false,
          enableItemGallery: response.data.enableItemGallery || false,
          enableComments: response.data.enableComments || false,
          enableChatRooms: response.data.enableChatRooms || false,
          enableNotifications: response.data.enableNotifications || false,
          requireEmailVerification: response.data.requireEmailVerification || false,
          enableTradingCardsFeature: response.data.enableTradingCardsFeature !== undefined ? response.data.enableTradingCardsFeature : false,
          enableMangaFeature: response.data.enableMangaFeature !== undefined ? response.data.enableMangaFeature : false,
          enableComicsFeature: response.data.enableComicsFeature !== undefined ? response.data.enableComicsFeature : false,
          enableCustomCollections: response.data.enableCustomCollections !== undefined ? response.data.enableCustomCollections : false,
          enableFederation: response.data.enableFederation !== undefined ? response.data.enableFederation : false,
          // [seo-strip-start]
          enableDiscovery: response.data.enableDiscovery !== undefined ? response.data.enableDiscovery : false,
          // [seo-strip-end]
          enableQuotaHarvesting: response.data.enableQuotaHarvesting !== undefined ? response.data.enableQuotaHarvesting : true,
          enableBacklog: response.data.enableBacklog !== undefined ? response.data.enableBacklog : true,
          enableDNF: response.data.enableDNF !== undefined ? response.data.enableDNF : false,
          enableStreaks: response.data.enableStreaks !== undefined ? response.data.enableStreaks : false,
          enableDistribution: response.data.enableDistribution !== undefined ? response.data.enableDistribution : false,
          enableRicherReactions: response.data.enableRicherReactions !== undefined ? response.data.enableRicherReactions : false,
        };
        setFeatureFlags(flags);
        console.log('Feature flags set:', flags);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching feature flags:', err);
        if (err.code === 'ECONNABORTED') {
          setError('Request timed out. Using default settings.');
        } else if (err.response) {
          setError(`Server error: ${err.response.status}`);
        } else if (err.request) {
          setError('No response from server. Using default settings.');
        } else {
          setError(`Error: ${err.message}`);
        }
        
        // Use default values if there's an error — core collections default true
        setFeatureFlags({
          enableGamesFeature: true,
          enableConsolesFeature: true,
          enableLegoFeature: true,
          enableMoviesFeature: true,
          enableMusicFeature: true,
          enableBooksFeature: true,
          enableBoardGamesFeature: false,
          enableAiChatFeature: false,
          enableMessageBoardFeature: false,
          enableFollowSystem: false,
          enableActivityFeed: false,
          enableReputationSystem: false,
          enableTrustModeration: false,
          enableSuggestions: false,
          enableReviews: false,
          enableItemGallery: false,
          enableComments: false,
          enableChatRooms: false,
          enableNotifications: false,
          requireEmailVerification: false,
          enableTradingCardsFeature: false,
          enableMangaFeature: false,
          enableComicsFeature: false,
          enableCustomCollections: false,
          enableFederation: false,
          // [seo-strip-start]
          enableDiscovery: false,
          // [seo-strip-end]
          enableQuotaHarvesting: true,
          enableBacklog: true,
          enableDNF: false,
          enableStreaks: false,
          enableDistribution: false,
          enableRicherReactions: false,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchFeatureFlags();
  }, []);

  // Function to update a feature flag
  const updateFeatureFlag = async (flagName, value) => {
    try {
      setLoading(true);
      
      // Get token for authentication
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Update the feature flag using the configured axiosInstance
      await axiosInstance.put(`/settings/${flagName}`, 
        { value }, 
        {
          timeout: 5000
        }
      );
      
      // Update the local state
      setFeatureFlags(prev => ({
        ...prev,
        [flagName]: value
      }));
      
      setError(null);
      return true;
    } catch (err) {
      console.error(`Error updating feature flag ${flagName}:`, err);
      
      // If it's a 403 error (Forbidden), the user doesn't have admin privileges
      if (err.response && err.response.status === 403) {
        setError(`Admin privileges required to update feature flags`);
      } else {
        setError(`Failed to update ${flagName}`);
      }
      
      // For non-admin users, we'll still update the local state for the current session
      // This allows them to see the changes in the UI without actually saving to the database
      if (err.response && err.response.status === 403) {
        setFeatureFlags(prev => ({
          ...prev,
          [flagName]: value
        }));
        console.log(`Updated local feature flag ${flagName} to ${value} (not saved to database)`);
      }
      
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Provide the feature flags and a way to check if a feature is enabled
  const value = {
    featureFlags,
    isFeatureEnabled: (featureName) => featureFlags[featureName] || false,
    updateFeatureFlag,
    loading,
    error
  };

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

// Custom hook to use feature flags
export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
};

export default FeatureFlagsContext;
